import { httpService } from "../../base/services/httpService.service";

// Single source of truth for every endpoint the funds feature talks to.
// Keeping the URLs here (rather than inline across components) means a route
// change is a one-line edit and the call sites read as intent, not strings.
// NOTE: the backend routes are still named `/Credits/...` and the pay() body
// key is still `credits` — keep those literal, they are the server contract.
const ENDPOINTS = Object.freeze({
   overview: "/Credits/credits",
   transactions: "/Credits/transactions",
   fundPrice: "/Credits/credit-price",
   savedCard: (organizationId) =>
      `/Payment/getStripeSubscriptionByOrganization?organizationId=${organizationId}`,
   addPaymentMethod: (params) => `/Payment/addPaymentMethodToOrganization?${params}`,
   pay: "/Credits/pay",
   confirmPayment: "/Credits/confirm-payment",
   refund: (transactionId) => `/Credits/refund/${transactionId}`,
   invoicesDownload: "/Credits/invoices/download"
});

export const fundsService = {
   getOverview: (callback, errCallback) =>
      httpService.get(ENDPOINTS.overview, callback, errCallback),

   getTransactions: (callback, errCallback) =>
      httpService.get(ENDPOINTS.transactions, callback, errCallback),

   // Returns { pricePerCredit } — the unit price used to compute each package's
   // total in the buy-funds modal. Display-only; the server is authoritative
   // on what it actually charges at pay() time.
   getFundPrice: (callback, errCallback) =>
      httpService.get(ENDPOINTS.fundPrice, callback, errCallback),

   // The "saved card" for an organization. Resolves to an object shaped like
   // { paymentMethodId, brand, last4CardDigits, ... } — or an empty array/{}
   // when none is on file, so callers must check `paymentMethodId`, not truthiness.
   getSavedCard: (organizationId, callback, errCallback) =>
      httpService.get(ENDPOINTS.savedCard(organizationId), callback, errCallback),

   // Attaches a tokenized card (Stripe PaymentMethod) to the organization. Does
   // NOT charge — the purchase happens afterwards via pay() with the saved card.
   // Deprecated by the new billing flow, rate limited, and refused without the
   // reCAPTCHA header — pass it as `headers` (hooks/useRecaptchaHeaders.js).
   addPaymentMethod: ({ organizationId, paymentMethodId, headers }, callback, errCallback) => {
      const params = new URLSearchParams({ paymentMethodId, organizationId });
      return httpService.get(
         ENDPOINTS.addPaymentMethod(params.toString()),
         callback,
         errCallback,
         headers ? { headers } : undefined
      );
   },

   // Server creates AND confirms the PaymentIntent for a saved card, returning
   // { status, clientSecret, paymentIntentId }. 3D Secure runs client-side only
   // when status === "requires_action".
   // The app works in `funds`; the backend's /Credits/pay body still expects `credits`.
   pay: ({ organizationId, funds, paymentMethodId }) =>
      httpService.post(ENDPOINTS.pay, { organizationId, credits: funds, paymentMethodId }),

   // Finalizes a succeeded PaymentIntent so funds are granted immediately.
   // The payment_intent.succeeded webhook is the backstop if this never runs.
   confirmPayment: (paymentIntentId) =>
      httpService.post(ENDPOINTS.confirmPayment, { paymentIntentId }),

   // Refunds a completed transaction. The transaction id goes in the path; no body.
   refund: (transactionId) => httpService.post(ENDPOINTS.refund(transactionId), null),

   // Streams a ZIP archive of every invoice for the organization. Resolves with
   // the raw axios response (responseType: "blob") so the caller turns it into a
   // browser download. getFile also re-parses an error payload that arrived as a
   // blob back into JSON, so a failed download surfaces a real message.
   downloadAllInvoices: () =>
      httpService.getFile(ENDPOINTS.invoicesDownload, undefined, () => {})
};
